// Faceoff spots — the 9 NHL faceoff dots, plus per-spot player layouts.
//
// Owns:   the static geometry of where faceoffs happen on the rink, and
//         the spawn template each team's 5 skaters take when a faceoff is
//         held at a given dot.
// Reads:  tuning.rink for rink dimensions.
// Mutates: player.pos / .vel / .moveDir / .faceDir / .stickDir / .action /
//         every per-tick reset field (when called by `layoutFaceoffPositions`).
// Emits:  no events.
//
// In real NHL hockey there are 9 dots:
//   - Centre (1)
//   - Neutral-zone dots near each blue line (4 total, 2 per blue line)
//   - End-zone dots in each defensive zone (4 total, 2 per end)
//
// Naming convention: "Left" and "Right" are from HOME's POV looking down
// +x. HOME's left side = +y world coords. The four NZ dots are split by
// which blue line they sit near (Home's blue line at x=-12, Away's at
// x=+12). The four end-zone dots sit deeper into each defensive zone.

import type { TeamId } from "@arcade-hockey/shared";
import { tuning } from "./tuning.js";
import type { PlayerState } from "./types.js";

export type FaceoffSpot =
  | "Center"
  | "NeutralHomeLeft"
  | "NeutralHomeRight"
  | "NeutralAwayLeft"
  | "NeutralAwayRight"
  | "HomeZoneLeft"
  | "HomeZoneRight"
  | "AwayZoneLeft"
  | "AwayZoneRight";

export const FACEOFF_SPOTS: ReadonlyArray<FaceoffSpot> = [
  "Center",
  "NeutralHomeLeft",
  "NeutralHomeRight",
  "NeutralAwayLeft",
  "NeutralAwayRight",
  "HomeZoneLeft",
  "HomeZoneRight",
  "AwayZoneLeft",
  "AwayZoneRight",
];

// Constants derived from rink geometry. Real NHL dots are 5 ft (≈1.5 m)
// from each blue line and 22 ft (≈6.7 m) apart laterally; end-zone dots
// are 20 ft (≈6.1 m) from the goal line. We pick clean integers that
// preserve the relative geometry inside our 60×30 m rink.
const NZ_DOT_X_FROM_BLUE = 2; // how far a NZ dot sits OFF its blue line into the NZ
const NZ_DOT_Y = 7; // lateral offset of NZ + end-zone dots from centre
const END_ZONE_DOT_X_FROM_CENTER = 20; // depth of an end-zone dot from centre ice
const BLUE_LINE_X = tuning.positioning.zones.blueLineX; // 12

/**
 * Returns the world-space (x, y) coordinates of a faceoff dot. Mutates
 * `out` in place — caller owns the buffer. Zero-allocation.
 */
export function faceoffSpotInto(out: { x: number; y: number }, spot: FaceoffSpot): void {
  switch (spot) {
    case "Center":
      out.x = 0;
      out.y = 0;
      return;
    case "NeutralHomeLeft":
      out.x = -(BLUE_LINE_X - NZ_DOT_X_FROM_BLUE);
      out.y = NZ_DOT_Y;
      return;
    case "NeutralHomeRight":
      out.x = -(BLUE_LINE_X - NZ_DOT_X_FROM_BLUE);
      out.y = -NZ_DOT_Y;
      return;
    case "NeutralAwayLeft":
      out.x = BLUE_LINE_X - NZ_DOT_X_FROM_BLUE;
      out.y = NZ_DOT_Y;
      return;
    case "NeutralAwayRight":
      out.x = BLUE_LINE_X - NZ_DOT_X_FROM_BLUE;
      out.y = -NZ_DOT_Y;
      return;
    case "HomeZoneLeft":
      out.x = -END_ZONE_DOT_X_FROM_CENTER;
      out.y = NZ_DOT_Y;
      return;
    case "HomeZoneRight":
      out.x = -END_ZONE_DOT_X_FROM_CENTER;
      out.y = -NZ_DOT_Y;
      return;
    case "AwayZoneLeft":
      out.x = END_ZONE_DOT_X_FROM_CENTER;
      out.y = NZ_DOT_Y;
      return;
    case "AwayZoneRight":
      out.x = END_ZONE_DOT_X_FROM_CENTER;
      out.y = -NZ_DOT_Y;
      return;
  }
}

// Per-team-per-role offset from the dot, applied at any faceoff spot.
// These match the existing centre-faceoff layout from `initial.ts` so a
// faceoff at "Center" produces bit-identical positions to the old
// `resetPlayersToSpawn` (the centre case is verified by unit test).
//
// For non-centre dots the same offsets place HOME deeper into HOME's
// defensive direction (more -x) and AWAY deeper into AWAY's defensive
// direction (more +x). For end-zone dots the player nearest a board may
// poke past it — the rink-clamp step (Step 8) won't fix it because
// resetPlayersToSpawn-style sets pos directly; we clamp here instead.
type RoleOffset = { dx: number; dy: number };
const HOME_OFFSETS: Record<PlayerState["position"], RoleOffset> = {
  C: { dx: -2, dy: 0 },
  LW: { dx: -4, dy: 6 },
  RW: { dx: -4, dy: -6 },
  LD: { dx: -10, dy: 3.5 },
  RD: { dx: -10, dy: -3.5 },
};
const AWAY_OFFSETS: Record<PlayerState["position"], RoleOffset> = {
  C: { dx: 2, dy: 0 },
  LW: { dx: 4, dy: 6 },
  RW: { dx: 4, dy: -6 },
  LD: { dx: 10, dy: 3.5 },
  RD: { dx: 10, dy: -3.5 },
};

const SPOT_SCRATCH = { x: 0, y: 0 };

/**
 * Snap every skater to the per-(team, role) faceoff position for `spot`.
 * Resets all per-tick state to the same values `resetPlayersToSpawn` uses
 * — zero velocity, Idle action, no stun/recovery, full stamina, no puck,
 * facing toward the enemy goal.
 *
 * For `spot === "Center"` the positions are bit-identical to
 * `resetPlayersToSpawn`. For non-centre dots, player positions are
 * shifted by the dot offset and clamped to keep them inside the rink.
 *
 * Mutates the players array in place. Zero-allocation past initial load.
 */
export function layoutFaceoffPositions(players: PlayerState[], spot: FaceoffSpot): void {
  faceoffSpotInto(SPOT_SCRATCH, spot);
  const sx = SPOT_SCRATCH.x;
  const sy = SPOT_SCRATCH.y;
  const halfL = tuning.rink.length / 2;
  const halfW = tuning.rink.width / 2;
  const r = tuning.player.radius;
  const minX = -halfL + r + 0.5; // 0.5 m extra to clear the goal-frame box
  const maxX = halfL - r - 0.5;
  const minY = -halfW + r;
  const maxY = halfW - r;

  for (const p of players) {
    const offsets = p.teamId === "home" ? HOME_OFFSETS : AWAY_OFFSETS;
    const off = offsets[p.position];
    let x = sx + off.dx;
    let y = sy + off.dy;
    if (x < minX) x = minX;
    else if (x > maxX) x = maxX;
    if (y < minY) y = minY;
    else if (y > maxY) y = maxY;
    p.pos.x = x;
    p.pos.y = y;
    p.vel.x = 0;
    p.vel.y = 0;
    p.moveDir.x = 0;
    p.moveDir.y = 0;
    const fx = p.teamId === "home" ? 1 : -1;
    p.faceDir.x = fx;
    p.faceDir.y = 0;
    p.stickDir.x = fx;
    p.stickDir.y = 0;
    p.action = "Idle";
    p.actionTimer = 0;
    p.stunTicks = 0;
    p.recoveryTicks = 0;
    p.stamina = 1.0;
    p.hasPuck = false;
    p.possessionQuality = 0;
    p.shootHoldTicks = 0;
    p.passHoldTicks = 0;
    p.receivedPassFromTick = -1;
    p.isProtecting = false;
  }
}

// Hash mapping — used by `core/SimTick.ts:hashRulesInto` so changes to
// `state.rules.nextFaceoffSpot` are reflected in the per-tick hash.
export function faceoffSpotToInt(spot: FaceoffSpot): number {
  switch (spot) {
    case "Center":
      return 0;
    case "NeutralHomeLeft":
      return 1;
    case "NeutralHomeRight":
      return 2;
    case "NeutralAwayLeft":
      return 3;
    case "NeutralAwayRight":
      return 4;
    case "HomeZoneLeft":
      return 5;
    case "HomeZoneRight":
      return 6;
    case "AwayZoneLeft":
      return 7;
    case "AwayZoneRight":
      return 8;
  }
}

/**
 * Choose the nearest faceoff dot to a given world position. Used by rules
 * that need to drop the puck at "the dot closest to where the rule was
 * called" (e.g., icing at the offending team's DZ dot on the puck's side;
 * offside at the NZ dot on the carrier's side).
 *
 * `teamPerspective` lets the rule narrow the search — e.g. icing only
 * lands in the offending team's DZ. Pass `null` for an unfiltered search.
 *
 * Zero-allocation.
 */
export function nearestFaceoffSpot(
  worldX: number,
  worldY: number,
  filter: ReadonlyArray<FaceoffSpot> | null,
): FaceoffSpot {
  const candidates = filter ?? FACEOFF_SPOTS;
  let best: FaceoffSpot = "Center";
  let bestDistSq = Number.POSITIVE_INFINITY;
  for (const spot of candidates) {
    faceoffSpotInto(SPOT_SCRATCH, spot);
    const dx = SPOT_SCRATCH.x - worldX;
    const dy = SPOT_SCRATCH.y - worldY;
    const d = dx * dx + dy * dy;
    if (d < bestDistSq) {
      bestDistSq = d;
      best = spot;
    }
  }
  return best;
}

/**
 * The four end-zone dots for a team's defensive zone. Used by icing /
 * goalie-freeze rules that always result in a DZ faceoff for the
 * offending team.
 */
export function defensiveZoneSpots(teamId: TeamId): ReadonlyArray<FaceoffSpot> {
  return teamId === "home"
    ? (["HomeZoneLeft", "HomeZoneRight"] as const)
    : (["AwayZoneLeft", "AwayZoneRight"] as const);
}

/**
 * The four neutral-zone dots near a given blue line. Used by offside,
 * which faces off at the NZ dot on the side the puck entered the OZ.
 *
 * `attackingTeam` = the team whose attacking blue line was crossed. NZ
 * dots near that blue line are on the carrier's side of centre ice.
 */
export function neutralZoneSpotsFor(attackingTeam: TeamId): ReadonlyArray<FaceoffSpot> {
  return attackingTeam === "home"
    ? (["NeutralAwayLeft", "NeutralAwayRight"] as const)
    : (["NeutralHomeLeft", "NeutralHomeRight"] as const);
}
